import { inject, onMounted, provide, defineComponent, computed } from '@vue/composition-api';
import { dialogController2ModalController, vantController } from './modal-controller';
import { dialogActions, useDialogReducer } from './use-dialog-reducer';
import { uniqueId } from 'lodash';
import Vue from 'vue';
export class Dialog {
  static install(options) {
    const { modalControllerAdapter } = options;
    Dialog._ModalComponent = options.ModalComponent;
    if (modalControllerAdapter) {
      Dialog.modalControllerAdapter = modalControllerAdapter;
    }
  }

  static _ModalComponent;
  static get ModalComponent() {
    if (!Dialog._ModalComponent) throw new Error('请先调用Dialog.install');
    return Dialog._ModalComponent;
  }

  static modalControllerAdapter = vantController;

  static dialogDispatch;
  _ComponentWithDialogContext;
  get ComponentWithDialogContext() {
    if (!this._ComponentWithDialogContext) throw new Error('ComponentWithDialogContext未初始化');
    return this._ComponentWithDialogContext;
  }

  static open(ComponentOrVnode, ComponentPropsOrModalProps, modalProps) {
    const dialogInstance = new Dialog();
    dialogInstance.create(ComponentOrVnode, ComponentPropsOrModalProps, modalProps);
    return dialogInstance.show();
  }

  static clearDialogs() {
    Dialog.dialogDispatch(dialogActions.clearDialogs());
  }

  id = uniqueId('promise-dialog-');

  dialogController = null;

  dialogPromise = {};
  onConfirmCallback;
  onCancelCallback;

  create(Component, componentProps, modalProps) {
    if (isVNode(Component)) {
      this.createByVnode(Component, componentProps);
    } else {
      this.createByComponent(Component, componentProps, modalProps);
    }
  }

  createByVnode(vnode, modalProps = {}) {
    const Component = defineComponent({
      render(h) {
        return h(vnode);
      }
    });
    this.createByComponent(Component, modalProps);
  }

  createByComponent(Component, componentProps, modalProps = {}) {
    const { dialogify } = Component;
    const ComponentWithContext = defineComponent({
      setup: () => {
        const appContext = inject(DialogAppContext);
        provide(DialogContext, this);

        const onOk = async () => {
          await (this.onConfirmCallback && this.onConfirmCallback(this));
          this.hide();
        };

        const onCancel = async () => {
          await (this.onCancelCallback && this.onCancelCallback(this));
          this.hide();
        };

        const afterClose = async () => {
          Dialog.dialogDispatch(dialogActions.popDialog(this));
        };

        const visible = computed(() => {
          return (
            (appContext.value && appContext.value.showDialogs && appContext.value.showDialogs.includes(this)) || false
          );
        });
        this.dialogController = {
          visible,
          onOk,
          onCancel,
          afterClose
        };

        const modalController = dialogController2ModalController(this.dialogController, Dialog.modalControllerAdapter);

        const computedModalProps = {
          ...modalController
        };
        const computedModalAttrs = {
          ...dialogify,
          ...modalProps
        };

        return {
          visible,
          computedModalProps,
          computedModalAttrs,
          componentProps
        };
      },
      render(h) {
        return h(Dialog.ModalComponent, {
          props: { ...this.computedModalProps, visible: this.visible },
          attrs: this.computedModalAttrs,
          scopedSlots: {
            default: () => {
              return h(Component, { props: this.componentProps });
            }
          }
        });
      }
    });
    this._ComponentWithDialogContext = ComponentWithContext;
    Dialog.dialogDispatch(dialogActions.pushDialog(this));
  }

  async show() {
    return new Promise((resolve, reject) => {
      this.dialogPromise.resolve = resolve;
      this.dialogPromise.reject = reject;
      Dialog.dialogDispatch(dialogActions.showDialog(this));
    });
  }

  hide() {
    Dialog.dialogDispatch(dialogActions.hideDialog(this));
  }

  onConfirm(callback) {
    this.onConfirmCallback = callback;
  }

  onCancel(callback) {
    this.onCancelCallback = callback;
  }

  confirm = resolveResult => {
    this.dialogPromise.resolve && this.dialogPromise.resolve(resolveResult);
    this.hide();
  };

  cancel = rejectResult => {
    this.dialogPromise.reject && this.dialogPromise.reject(rejectResult);
    this.hide();
  };
}

export const DialogContext = Symbol('DialogContext');

const DialogAppContext = Symbol('DialogAppContext');
export const DialogProvider = defineComponent({
  props: {
    ModalComponent: {
      type: Object,
      required: true
    },
    modalControllerAdapter: {
      type: Function
    }
  },
  setup(props) {
    const ModalComponent = props.ModalComponent;
    const { modalControllerAdapter } = props;
    const [store, dispatch] = useDialogReducer();
    provide(DialogAppContext, store);

    onMounted(() => {
      Dialog.install({ ModalComponent, modalControllerAdapter });
      Dialog.dialogDispatch = dispatch;
    });

    return {
      store
    };
  },
  render(h) {
    return h('div', {}, [
      this.$slots.default,
      this.store.dialogs.map((dialog, i) => {
        const { ComponentWithDialogContext } = dialog;
        return h(ComponentWithDialogContext, { key: i });
      })
    ]);
  }
});

function isVNode(node) {
  return node !== null && typeof node === 'object' && !!node.componentOptions;
}
